import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box } from '@atlaskit/primitives';
import { makeAppKey } from '@atlassian/mpac-api-client';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { getPricingPlanItems, makePaidAppPricing, PAID_PRICING_TYPE } from '@atlassian/mpac-pricing-utils';
import { Stripe, useAppPlans, useGetGeoIPDetails } from '@atlassian/mpac-primitives';
import { usePricingPlan } from '@atlassian/mpac-site-selection';
import { makeAutoRenew, makeEvaluationLicenseType, makeNotAutoRenew, makePaidPaymentModel, makeSubscriptionLicenseType } from '@atlassian/mpac-spi';
import { MarketplaceStoreBillingSystem, MarketplaceStoreEditionType, MarketplaceStoreHostInstanceType } from '@atlassian/mpac-types/graphql-gateway-types';
import { PaidPricing } from '@atlassian/mpac-ui';
import { DateUtils } from '@atlassian/mpac-utils';
import { Either, Option } from 'funfix-core';
import { List } from 'immutable';
import { jsx as _jsx } from "react/jsx-runtime";
const getSubscriptionPeriod = isAnnual => isAnnual ? 'annual' : 'monthly';
const getRenewalType = isAutoRenewal => isAutoRenewal ? 'auto' : 'quote';
const getInstanceType = type => type === MarketplaceStoreHostInstanceType.Sandbox ? 'sandbox' : 'production';
const getHostBillingSystem = billingSystem => billingSystem === MarketplaceStoreBillingSystem.Hams ? 'HAMS' : 'CCP';
// eslint-disable-next-line max-lines-per-function
export const PaidAppPricing = props => {
  var _props$installedAppDe, _props$cmtAvailabilit, _props$selectedPlan, _props$selectedPlan2;
  const {
    billingCurrency,
    hostLicense,
    upmLicensedHostUsers,
    billingSystem,
    hostCmtEnabled,
    instanceType
  } = props.hostStatus.marketplaceStore.hostStatus;
  const {
    subscriptionAnnual,
    evaluation,
    autoRenewal,
    maximumNumberOfUsers
  } = hostLicense;
  const installedApp = (_props$installedAppDe = props.installedAppDetailsResult) === null || _props$installedAppDe === void 0 ? void 0 : _props$installedAppDe.marketplaceStore.installedAppDetails;
  const cmtAvailability = (_props$cmtAvailabilit = props.cmtAvailabilityResult) === null || _props$cmtAvailabilit === void 0 ? void 0 : _props$cmtAvailabilit.marketplaceStore.cmtAvailability;
  const {
    enableLdCustomerGaChanges
  } = useStatsig();
  const getCurrentLicensedUsers = function getCurrentLicensedUsers() {
    let areAllUsersSelected = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    let selectedUsersCount = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    if (enableLdCustomerGaChanges()) {
      // If explicitly selecting all users, return the host's licensed user count
      if (areAllUsersSelected) {
        return upmLicensedHostUsers;
      }

      // If a specific user count is provided and valid, use it
      if (selectedUsersCount > 0) {
        return selectedUsersCount;
      }
    }

    // Fallback to host licensed users for all other cases
    return upmLicensedHostUsers;
  };
  const {
    pricingPlanResult,
    pricingPlanPending,
    pricingPlanError
  } = usePricingPlan(props.appDetails.appId, billingCurrency);
  const {
    countryCode
  } = useGetGeoIPDetails();
  const {
    data: editionsWithPricingResult,
    loading: editionsWithPricingPending,
    error: editionsWithPricingError
  } = useAppPlans({
    appId: props.appDetails.appId
  }, countryCode);
  if (pricingPlanPending || props.showAppPlans && editionsWithPricingPending) {
    return /*#__PURE__*/_jsx(Stripe, {
      width: "5rem",
      height: "0.875rem",
      marginTop: "var(--ds-space-075, 6px)"
    });
  }
  if (pricingPlanError || !pricingPlanResult || props.showAppPlans && editionsWithPricingError) {
    return /*#__PURE__*/_jsx(Box, {
      children: "Unknown Price"
    });
  }
  const renewalType = getRenewalType(autoRenewal);
  const hostPricingInstanceType = getInstanceType(instanceType);
  const hostBillingSystem = getHostBillingSystem(billingSystem);
  const pricingPlanItems = getPricingPlanItems(pricingPlanResult);
  const eligibleForFreeOnSandbox = installedApp && installedApp.paidLicenseActiveOnParent;
  const createSubscriptionLicenseType = expiryDate => makeSubscriptionLicenseType({
    type: 'subscription',
    renewalType: autoRenewal ? makeAutoRenew() : makeNotAutoRenew({
      isAutoRenewal: false,
      endDate: expiryDate ? new Date(expiryDate).getTime() : 0
    })
  });
  const createEvaluationLicenseType = expiryDate => makeEvaluationLicenseType({
    type: 'evaluation',
    expiry: new Date(expiryDate).getTime(),
    isAutoRenewal: autoRenewal
  });
  const getPaidPaymentModel = () => {
    if (!installedApp || !installedApp.licenseActive) {
      return Option.none();
    }
    const licenseType = !installedApp.licenseExpiryDate || !DateUtils.isTodayOrFutureDate(new Date(installedApp.licenseExpiryDate)) ? Either.right(createSubscriptionLicenseType(installedApp.licenseExpiryDate)) : Either.left(createEvaluationLicenseType(installedApp.licenseExpiryDate));
    return Option.some(makePaidPaymentModel({
      type: 'paid',
      licenseType
    }));
  };
  const paidPricingProps = {
    appKey: makeAppKey(props.appDetails.appKey),
    hostPricing: {
      evaluation,
      currentLicensedUsers: getCurrentLicensedUsers(props.areAllUsersSelected, props.selectedUsersCount),
      maximumLicensedUsers: maximumNumberOfUsers,
      renewalType,
      subscriptionPeriod: getSubscriptionPeriod(subscriptionAnnual),
      billingCurrency,
      instanceType: hostPricingInstanceType,
      billingSystem: hostBillingSystem,
      hostCmtEnabled: Boolean(hostCmtEnabled)
    },
    paidPaymentModel: getPaidPaymentModel(),
    links: props.links,
    paidAppPricing: makePaidAppPricing({
      type: PAID_PRICING_TYPE,
      annual: List(pricingPlanItems.annual),
      monthly: List(pricingPlanItems.monthly)
    }),
    eligibleForFreeOnSandbox,
    cloudMigrationTrial: _objectSpread({
      isEligible: Boolean(cmtAvailability === null || cmtAvailability === void 0 ? void 0 : cmtAvailability.allowed)
    }, (cmtAvailability === null || cmtAvailability === void 0 ? void 0 : cmtAvailability.maintenanceEndDate) && {
      btfSenEndDate: new Date(cmtAvailability.maintenanceEndDate)
    }),
    editions: editionsWithPricingResult === null || editionsWithPricingResult === void 0 ? void 0 : editionsWithPricingResult.editions,
    selectedEditionType: (_props$selectedPlan = props.selectedPlan) === null || _props$selectedPlan === void 0 ? void 0 : _props$selectedPlan.value,
    eligibleOfferings: props.eligibleOfferings,
    hideTryFreeHeading: ((_props$selectedPlan2 = props.selectedPlan) === null || _props$selectedPlan2 === void 0 ? void 0 : _props$selectedPlan2.value) === MarketplaceStoreEditionType.Free
  };
  return /*#__PURE__*/_jsx(PaidPricing, _objectSpread(_objectSpread({}, paidPricingProps), {}, {
    alignPaidPricingContentLeftOnMobile: true
  }));
};